import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

// Components
import {
  Avatar,
  Badge,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Popover,
  Space,
} from 'antd';

// Constants
import {
  APP_ENV,
  DEFAULT_ENVIRONMENT,
  DEFAULT_FILE_PREFIX,
  DEFAULT_LOGO,
  DEFAULT_LOGO_HEIGHT,
  HRMS_TYPE,
  OPPORTUNE_CLIENT,
} from '../../constant';

// Icons
import LogoImg from '../../assets/elrin-logo.png';
import ProfileIcon from '../../assets/icons/ProfileIcon';
import AnswersIcon from '../../assets/icons/AnswerIcon';
import {
  BellOutlined,
  CalendarOutlined,
  DiffOutlined,
  DownOutlined,
  HomeOutlined,
  InteractionOutlined,
  MessageOutlined,
  QuestionOutlined,
  SlidersOutlined,
  TagsOutlined,
} from '@ant-design/icons';

// hooks
import { useSession } from '../../Providers/SessionProviders';
import useWindowSize from '../../hooks/window';

// utils
import { translate } from '../../i18n/i18n';
import {
  generateLinkText,
  getShortName,
  removeAuthToken,
  stringToColor,
} from '../../utils';

const { Header, Content, Sider } = Layout;

const t = translate(['header', 'sidebar']);

const StyledLayout = styled(Layout)`
  height: 100vh;

  .ant-layout-content {
    overflow-x: hidden;
  }
`;

const StyledContent = styled(Content)`
  // height: calc(100vh - 65px);
  background: #f5f5f5;
  overflow: auto;

  @media (max-width: 768px) {
    margin: 0px 10px 0px 10px;
  }
`;

const MainWrapper = styled.div`
  margin: 15px 15px 0px 15px;
`;

const StyledSider = styled(Sider)`
  background-color: #edf2fc;

  &&& .ant-menu {
    margin-top: 20px;
    border-inline-end: none;
    color: #617080;
  }
  .ant-menu-item:hover {
    border-radius: 0px;
  }
  .ant-layout-sider-children,
  .ant-menu-light {
    background-color: #edf2fc;
  }
  .ant-menu-item-selected {
    border-left: 5px solid ${(props) => props.theme.colorPrimary};
    border-radius: 0px;
    background: #fff;
  }
  svg {
    color: ${(props) => props.theme.colorPrimary};
  }
`;

const StyledHeader = styled(Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  background-color: #fff;
  border-bottom: 1px solid #e2e3e3;

  img {
    margin: 20px 0px 15px 30px;
    height: ${DEFAULT_LOGO_HEIGHT}px;
    width: auto;
  }

  .ant-dropdown-trigger {
    color: ${(props) => props.theme.colorPrimary};
    margin-right: 10px;
  }
`;

const StyledAvatar = styled(Avatar)`
  background: ${({ name }) => (name ? stringToColor(name) : undefined)};
`;

const SidebarMenu = styled(Menu)`
  .ant-menu-title-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  svg {
    color: ${(props) => props.theme.colorPrimary} !important;

    path {
      fill: ${(props) => props.theme.colorPrimary} !important;
    }
  }
`;

const StyleBadge = styled(Badge)`
  .ant-badge-count {
    background-color: ${(props) => props.theme.colorPrimary} !important;
  }
`;

const NotificationIcon = styled(BellOutlined)`
  color: ${(props) => props.theme.colorPrimary};
  font-size: 22px;
  cursor: pointer;
  margin-top: 5px;
`;

const StyledPopover = styled(Popover)`
  .ant-popover-arrow {
    right: 0px;
  }
`;

// const StyledMenu = styled(Menu)`
//   color: #617080;
//   .ant-menu-item:hover {
//     border-radius: 0px;
//   }
//   svg {
//     color: #617080;
//   }
// `;

// const BackyLogo = styled.img`
//   &&& {
//     margin: 20px 0px;
//   }
// `;

const AuthLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, currentUser, setCurrentUser } = useSession();
  const { width } = useWindowSize();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    document.getElementById('layout').scroll(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getSelectedKey = () => {
    const keys = {
      '': 'dashboard',
      'suggestion-box': 'suggestion-box',
    };
    const index = [DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV) ? 2 : 1;
    const pathname = location.pathname.split('/')[index];
    return keys[pathname] || location.pathname.split('/')[index];
  };

  const onLogout = () => {
    setCurrentUser(null);
    navigate(generateLinkText('/login'));
    removeAuthToken();
  };

  const getHeaderDropdownItems = () => {
    if (
      OPPORTUNE_CLIENT.includes(company?.slug) &&
      [HRMS_TYPE.OPPORTUNE].includes(company?.hrms_type)
    ) {
      return [
        {
          label: <a onClick={() => onLogout()}>{t('logout')}</a>,
          key: '4',
        },
      ];
    }
    return [
      {
        label: (
          <NavLink to={generateLinkText('/profile/edit')}>
            {t('profile')}
          </NavLink>
        ),
        key: '0',
      },
      {
        label: (
          <NavLink to={generateLinkText('/profile/company')}>
            {t('editCompany')}
          </NavLink>
        ),
        key: '1',
      },
      {
        label: (
          <NavLink to={generateLinkText('/profile/change-password')}>
            {t('changePassword')}
          </NavLink>
        ),
        key: '2',
      },
      {
        type: 'divider',
        key: '3',
      },
      {
        label: <a onClick={() => onLogout()}>{t('logout')}</a>,
        key: '4',
      },
    ];
  };

  const onCollapse = (value) => {
    if (width <= 500) {
      setCollapsed(value);
    }
  };

  const logo = useMemo(() => {
    if (DEFAULT_LOGO) return DEFAULT_LOGO;
    if (company?.logo) return `${DEFAULT_FILE_PREFIX}${company.logo}`;
    return LogoImg;
  }, [company]);

  const appRoutes = useMemo(() => {
    const routes = [
      {
        icon: <HomeOutlined />,
        key: 'dashboard',
        link: '/',
        title: t('dashboard'),
      },
      {
        key: 'suggestion-box',
        icon: <MessageOutlined />,
        link: '/suggestion-box',
        title: t('suggestionBox'),
        showBadge: true,
        ignore_hrms: [HRMS_TYPE.OPPORTUNE],
        is_manager: true,
      },
      {
        key: 'survey-schema',
        icon: <DiffOutlined />,
        link: '/survey-schema',
        title: t('surveySchema'),
      },
      {
        key: 'questions',
        icon: <QuestionOutlined />,
        link: '/questions',
        title: t('questions'),
        is_manager: true,
      },
      {
        key: 'answers',
        icon: <AnswersIcon />,
        link: '/answers',
        title: t('answer'),
        is_manager: true,
      },
      {
        key: 'employees',
        icon: <ProfileIcon />,
        link: '/employees',
        title: t('employees'),
      },
      {
        key: 'tags',
        icon: <TagsOutlined />,
        link: '/tags',
        title: t('tags'),
        ignore_hrms: ['bgss'],
      },
    ];
    if (company?.enable_journey) {
      routes.push({
        key: 'journey',
        icon: <SlidersOutlined />,
        link: '/journey',
        title: t('journey'),
      });
    }
    if (company?.enable_events) {
      routes.push({
        key: 'events',
        icon: <CalendarOutlined />,
        link: '/events',
        title: t('events'),
      });
    }
    if (company?.loop_enabled) {
      routes.push({
        key: 'loop',
        icon: <InteractionOutlined />,
        link: '/loop',
        title: t('loop'),
      });
    }
    return routes
      .filter(
        (ele) =>
          !(
            (ele.ignore_hrms || []).includes(company?.hrms_type) ||
            (ele.ignore_hrms || []).includes(company?.slug)
          ),
      )
      .filter((ele) => {
        if (currentUser.is_company_admin) return true;
        return ele.is_manager;
      });
  }, [company, currentUser]);

  return (
    <>
      <StyledLayout>
        <StyledHeader>
          <div style={{ display: 'flex', gap: '20px' }}>
            <img
              src={logo}
              alt="logo"
              style={
                company?.slug === 'shriramproperties' ? { height: '60px' } : {}
              }
            />
          </div>
          <Dropdown
            menu={{ items: getHeaderDropdownItems() }}
            trigger={['click']}>
            <a onClick={(e) => e.preventDefault()}>
              <Space>
                <StyledAvatar name={currentUser?.name}>
                  {getShortName(currentUser?.name)}
                </StyledAvatar>
                {currentUser?.name}
                <DownOutlined />
              </Space>
            </a>
          </Dropdown>
        </StyledHeader>
        <Layout>
          <StyledSider
            theme="light"
            breakpoint="md"
            width="220"
            collapsedWidth="0"
            collapsed={width <= 500 ? collapsed : false}
            // onBreakpoint={(broken) => {
            //   console.log(broken);
            // }}
            onCollapse={(value) => onCollapse(value)}>
            <SidebarMenu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              items={appRoutes.map((item) => ({
                key: item.key,
                icon: item.icon,
                label: (
                  <>
                    <NavLink to={generateLinkText(item.link)}>
                      {item.title}
                    </NavLink>
                    {item?.showBadge && currentUser?.unread_count > 0 && (
                      <StyleBadge
                        count={currentUser?.unread_count}
                        shape="square"
                        overflowCount={10}
                        offset={10}
                      />
                    )}
                  </>
                ),
                onClick: () => onCollapse(!collapsed),
              }))}
            />
            {/* <Divider />
            <StyledMenu
              theme="light"
              mode="inline"
              selectable={false}
              items={SupportItems.map((item) => ({
                key: item.key,
                icon: item.icon,
                label: item.title,
                disabled: item.disabled,
                onClick: () => onCollapse(!collapsed),
              }))}
            /> */}
          </StyledSider>
          <StyledContent id="layout">
            <MainWrapper>{children}</MainWrapper>
          </StyledContent>
        </Layout>
      </StyledLayout>
    </>
  );
};

AuthLayout.propTypes = {
  children: PropTypes.node,
};

export default AuthLayout;
